import { Post } from "../models/Post";
import { PlatformType } from "../models/Platform";

export interface ConnectionTimeInfo {
  connectionId: string;
  platform: PlatformType;
  scheduledFor?: number;
  publishedAt?: number;
  status: string;
}

export const isSameDay = (ts1: number, ts2: number): boolean => {
  const d1 = new Date(ts1);
  const d2 = new Date(ts2);
  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  );
};

export const analyzeConnectionTimes = (
  post: Post
): {
  hasMultipleTimes: boolean;
  hasMixedStates: boolean;
  allSameDay: boolean;
  connectionTimes: ConnectionTimeInfo[];
} => {
  const connectionTimes: ConnectionTimeInfo[] = Object.entries(
    post.connections
  ).map(([connectionId, conn]) => ({
    connectionId,
    platform: conn.platform,
    scheduledFor: conn.scheduledFor ?? post.scheduledFor,
    publishedAt: conn.publishedAt,
    status: conn.status,
  }));

  const hasPublished = connectionTimes.some((c) => c.status === "published");
  const hasPending = connectionTimes.some((c) => c.status === "pending");
  const hasMixedStates = hasPublished && hasPending;

  const pendingTimes = connectionTimes
    .filter((c) => c.status !== "published" && c.scheduledFor)
    .map((c) => c.scheduledFor!);

  // Group times that are within 1 minute of each other as "the same"
  const TIME_TOLERANCE_MS = 60 * 1000;
  const normalizedTimes: number[] = [];

  for (const time of pendingTimes) {
    const existingMatch = normalizedTimes.find(
      (t) => Math.abs(t - time) < TIME_TOLERANCE_MS
    );
    if (!existingMatch) {
      normalizedTimes.push(time);
    }
  }

  const hasMultipleTimes = normalizedTimes.length > 1;

  const allSameDay =
    normalizedTimes.length <= 1 ||
    normalizedTimes.every((t) => isSameDay(t, normalizedTimes[0]));

  return { hasMultipleTimes, hasMixedStates, allSameDay, connectionTimes };
};
